import './App.css';
import React, { useState, useRef, useEffect } from 'react';
import { EyeIcon, RefreshIcon, BackIcon } from './Icons';

function shuffle(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

export default function FlashcardsApp({ words, onBack }) {
  const [shuffledWords, setShuffledWords] = useState(() => shuffle(words));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState({});
  const [completed, setCompleted] = useState(false);
  const [toRepeat, setToRepeat] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);
  const [isHardMode, setIsHardMode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const timerRef = useRef(null);
  
  const currentWord = shuffledWords[currentIndex];
  const mistakeCount = mistakes[currentIndex] || 0;

  const checkAnswer = () => {
    if (isHardMode) {
      clearInterval(timerRef.current);
    }

    const input = userInput.trim().toLowerCase();
    const correct = currentWord.de.toLowerCase();

    if (input === correct) {
      setIsCorrect(true);
      setTimeout(() => {
        setIsCorrect(null);
        setUserInput('');
        setShowHint(false);
        nextWord();
        inputRef.current?.focus();
      }, 800);
    } else {
      setIsCorrect(false);
      const newMistakes = { ...mistakes };
      newMistakes[currentIndex] = (newMistakes[currentIndex] || 0) + 1;
      setMistakes(newMistakes);
      setScore((s) => s + 1);

      if (newMistakes[currentIndex] >= 5) {
        setTimeout(() => {
          setIsCorrect(null);
          setUserInput('');
          setShowHint(false);
          nextWord();
          inputRef.current?.focus();
        }, 1000);
      }
    }
  };

  const nextWord = () => {
    if (currentIndex + 1 < shuffledWords.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      const repeat = Object.entries(mistakes)
        .filter(([_, count]) => count >= 5)
        .map(([index]) => shuffledWords[Number(index)].pl);
      setToRepeat(repeat);
      setCompleted(true);
    }
  };

  const restart = () => {
    setShuffledWords(shuffle(words));
    setCurrentIndex(0);
    setUserInput('');
    setIsCorrect(null);
    setScore(0);
    setMistakes({});
    setCompleted(false);
    setToRepeat([]);
    setShowHint(false);
  };

  const handleTimeout = () => {
    setIsCorrect(false);
    const newMistakes = { ...mistakes };
    newMistakes[currentIndex] = (newMistakes[currentIndex] || 0) + 1;
    setMistakes(newMistakes);
    setScore((s) => s + 1);

    setTimeout(() => {
      setIsCorrect(null);
      setUserInput('');
      setShowHint(false);
      nextWord();
      inputRef.current?.focus();
    }, 1000);
  };

  const handleSpecialCharClick = (char) => {
    setUserInput(prev => prev + char);
    inputRef.current?.focus();
  };

  const handleArticleClick = (article) => {
    setUserInput(prev => `${article} ${prev.trim()}`);
    inputRef.current?.focus();
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [currentIndex]);

  useEffect(() => {
    if (!isHardMode || completed) {
      clearInterval(timerRef.current);
      return;
    }

    setTimeLeft(10); // Reset timer
    timerRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          handleTimeout();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [currentIndex, isHardMode, completed]);


  if (completed) {
    return (
      <>
        <div className="flashcard-container">
          <div className="progress-container">
            <div className="progress-bar" style={{ width: `${Math.round((currentIndex + 1) / shuffledWords.length * 100)}%` }}></div>
          </div>
          <div style={{ marginBottom: '1rem', fontSize: '1rem', color: '#555' }}>
            Fiszka {currentIndex + 1} z {shuffledWords.length}
          </div>
          <h1>🎉 Gratulacje!</h1>
          <p>Punkty karne: <strong>{score}</strong></p>
          {toRepeat.length > 0 ? (
            <>
              <p>Słowa do powtórki:</p>
              <ul>{toRepeat.map((word, i) => <li key={i}>{word}</li>)}</ul>
            </>
          ) : <p>Nie masz słów do powtórki 🎉</p>}
          <button onClick={restart}><RefreshIcon /> Spróbuj jeszcze raz</button>
        </div>
        <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
      </>
    );
  }

  return (
    <>
      <div className={`flashcard-container ${isCorrect === true ? 'correct-answer' : ''} ${isCorrect === false ? 'wrong-answer' : ''}`}>
      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${Math.round((currentIndex + 1) / shuffledWords.length * 100)}%` }}></div>
      </div>
      <div style={{ marginBottom: '1rem', fontSize: '1rem', color: '#555' }}>
        Fiszka {currentIndex + 1} z {shuffledWords.length}
      </div>
      {isHardMode && (
        <div className={`timer ${timeLeft <= 3 ? 'danger' : ''}`}>
          {timeLeft}
        </div>
      )}
      <h1>✅ Sprawdź się</h1>
      <p style={{ fontWeight: 'bold' }}>
        Polskie słowo: <span style={{ fontWeight: 'normal' }}>{currentWord.pl}</span>
      </p>
      <div className="input-area">
        <input
          ref={inputRef}
          type="text"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && checkAnswer()}
          placeholder="Wpisz niemieckie tłumaczenie"
        />
        <div className="special-chars">
          <button onClick={() => handleSpecialCharClick('ä')}>ä</button>
          <button onClick={() => handleSpecialCharClick('ö')}>ö</button>
          <button onClick={() => handleSpecialCharClick('ü')}>ü</button>
          <button onClick={() => handleSpecialCharClick('ß')}>ß</button>
        </div>
      </div>
      <div className="article-buttons">
        <button onClick={() => handleArticleClick('der')}>der</button>
        <button onClick={() => handleArticleClick('die')}>die</button>
        <button onClick={() => handleArticleClick('das')}>das</button>
      </div>
      {isCorrect === false && <p style={{ color: 'red' }}>Spróbuj jeszcze raz!</p>}
      {isCorrect === true && <p style={{ color: 'green' }}>Poprawnie!</p>}

      <div className="action-buttons-container"><button onClick={checkAnswer}>Sprawdź</button></div>

      {mistakeCount >= 3 && !showHint && (
        <button onClick={() => setShowHint(true)}><EyeIcon /> Zobacz podpowiedź</button>
      )}

      {showHint && (
        <p className="hint"><strong>Podpowiedź:</strong> {currentWord.de}</p>
      )}

      <button onClick={() => setIsHardMode(prev => !prev)} className="hard-mode-toggle">{isHardMode ? '🔥 Hard' : '🧊 Normal'}</button>
      </div>
      <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
    </>
  );
}
